import {
  ArrowLeftIcon,
  BookTextIcon,
  Dialog,
  IconButton,
  NoteBlankIcon,
  ScanEyeIcon,
  Separator,
  TooltipGroup,
  VisuallyHidden,
  WandSparklesIcon,
} from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Client,
  Product,
} from '@buffer-mono/tracking-plan'
import { useQuery } from '@apollo/client/react'

import { TagSelector } from '~publish/components/Composer/components/TagSelector'
import {
  NotesIndicator,
  getNotesIndicatorLabel,
  getNotesUnreadTooltip,
} from '~publish/components/Notes'
import { useComposerNotesSubject } from '~publish/components/Composer/hooks/useComposerNotes'
import { useHasContentItemTitle } from '~publish/components/Composer/hooks/useHasContentItemTitle'
import {
  useActiveSidePanel,
  useComposerMode,
  useIsDraftMode,
  usePrefetchTemplates,
  useToggleSidePanel,
} from '~publish/components/Composer/store/appSlice/hooks'
import { capitalize } from '~publish/helpers/capitalize'
import { useCommonTrackingProperties } from '~publish/hooks/useCommonTrackingProperties'
import { GetPostForComposerNotes } from '~publish/hooks/notes/GetPostForComposerNotes'
import {
  usePostUnreadMentionCount,
  usePostUnreadNoteCount,
} from '~publish/hooks/notes/usePostNoteCounts'
import { toggleAIAssistant } from '~publish/legacy/ai/state/slice'
import { useBackToOmni } from '~publish/legacy/composer/composer/utils/useBackToOmni'
import { useAppDispatch } from '~publish/legacy/store'

import { BackToOmniConfirmationModal } from './BackToOmniConfirmationModal'
import { ContentItemTitle } from './ContentItemTitle'
import {
  ComposerHeaderCloseButton,
  ComposerHeaderMaximizeButton,
  ComposerHeaderNotesButton,
  ComposerHeaderPanelButton,
} from './ComposerHeaderActions'
import { ComposerHeaderLayout } from './ComposerHeaderLayout'

import styles from './Header.module.css'

import type { JSX } from 'react'

export const Header = ({
  isMaximized,
  canMaximize,
  onDismiss,
  onToggleMaximize,
}: {
  isMaximized?: boolean
  canMaximize?: boolean
  onDismiss?: () => void
  onToggleMaximize: () => void
}): JSX.Element => {
  const dispatch = useAppDispatch()

  const mode = useComposerMode()
  const draftMode = useIsDraftMode()
  const hasContentItemTitle = useHasContentItemTitle()
  const { backToOmni, canGoBackToOmni } = useBackToOmni()

  const activeSidePanel = useActiveSidePanel()
  const toggleSidePanel = useToggleSidePanel()
  const prefetchTemplates = usePrefetchTemplates()
  const notesSubject = useComposerNotesSubject()
  const notesPostId =
    notesSubject?.subjectType === 'post' ? notesSubject.subjectId : null
  // Keep the saved Post's summary in Apollo even while the panel is closed.
  useQuery(GetPostForComposerNotes, {
    variables: { id: notesPostId ?? '', includeNotesSummary: true },
    skip: !notesPostId,
    errorPolicy: 'all',
  })
  const unreadNotes = usePostUnreadNoteCount(notesPostId)
  const unreadMentions = usePostUnreadMentionCount(notesPostId)
  const commonTrackingProperties = useCommonTrackingProperties()

  const handleToggleTemplates = (): void => {
    if (activeSidePanel !== 'templates') {
      BufferTracker.cTAClicked({
        clientName: Client.PublishWeb,
        product: Product.Publish,
        cta: 'publish-composer-templates-openLibrary-1',
        upgradePathName: null,
        ...commonTrackingProperties,
      })
    }
    toggleSidePanel('templates')
  }

  return (
    <ComposerHeaderLayout
      sidePanelOpen={!!activeSidePanel}
      leading={
        canGoBackToOmni && (
          <BackToOmniConfirmationModal onConfirm={backToOmni}>
            <IconButton
              label="Back to previous step"
              variant="tertiary"
              tooltip="Back to previous step"
              data-back-button
            >
              <ArrowLeftIcon />
            </IconButton>
          </BackToOmniConfirmationModal>
        )
      }
      title={
        hasContentItemTitle ? (
          <ContentItemTitle
            fallback={`${capitalize(mode)} ${draftMode ? 'Draft' : 'Post'}`}
          />
        ) : (
          <>
            <Dialog.Title>{`${capitalize(mode)} ${
              draftMode ? 'Draft' : 'Post'
            }`}</Dialog.Title>
            <VisuallyHidden>
              <Dialog.Description>Create and edit posts</Dialog.Description>
            </VisuallyHidden>
          </>
        )
      }
      tags={<TagSelector maxVisibleTags={2} />}
      actions={
        // Fullscreen/Close use IconButton's built-in 800ms delay (tuned for
        // being hovered in isolation); Templates/AI Assistant/Preview default
        // to 300ms. TooltipGroup's own 150ms default overrides both, so which
        // icon you reach first doesn't change how long the first hover takes.
        <TooltipGroup>
          <ComposerHeaderPanelButton
            label="Templates"
            active={activeSidePanel === 'templates'}
            onClick={handleToggleTemplates}
            onMouseEnter={prefetchTemplates}
            onFocus={prefetchTemplates}
          >
            <BookTextIcon />
          </ComposerHeaderPanelButton>
          <ComposerHeaderPanelButton
            label="AI Assistant"
            active={activeSidePanel === 'aiAssistant'}
            onClick={() => {
              toggleSidePanel('aiAssistant')
              // Toggle AI assistant in redux as well as its internal state relies on it
              dispatch(toggleAIAssistant(activeSidePanel !== 'aiAssistant'))
            }}
          >
            <WandSparklesIcon />
          </ComposerHeaderPanelButton>
          <ComposerHeaderPanelButton
            label="Preview"
            active={activeSidePanel === 'postPreview'}
            onClick={() => toggleSidePanel('postPreview')}
          >
            <ScanEyeIcon />
          </ComposerHeaderPanelButton>
          {notesSubject && (
            <>
              <Separator
                decorative
                orientation="vertical"
                className={styles.actionsSeparator}
              />
              <ComposerHeaderNotesButton
                label={getNotesIndicatorLabel(unreadNotes)}
                tooltip={getNotesUnreadTooltip(unreadNotes, unreadMentions)}
                active={activeSidePanel === 'notes'}
                onClick={() => toggleSidePanel('notes')}
              >
                <NotesIndicator unreadCount={unreadNotes}>
                  <NoteBlankIcon />
                </NotesIndicator>
              </ComposerHeaderNotesButton>
            </>
          )}
          {canMaximize && (
            <ComposerHeaderMaximizeButton
              isMaximized={isMaximized ?? false}
              onToggle={onToggleMaximize}
            />
          )}
          {onDismiss && (
            <Dialog.Close>
              <ComposerHeaderCloseButton
                className={styles.closeButton}
                onClick={onDismiss}
              />
            </Dialog.Close>
          )}
        </TooltipGroup>
      }
    />
  )
}
